import React from 'react';
import { AlertTriangle, AlertCircle, HelpCircle } from 'lucide-react';
import type { ENumber } from '../lib/eNumberService';

interface ENumberWarningProps {
  eNumbers: {
    haramENumbers: ENumber[];
    mushboohENumbers: ENumber[];
    allENumbers: ENumber[];
  };
}

export const ENumberWarning: React.FC<ENumberWarningProps> = ({ eNumbers }) => {
  if (eNumbers.allENumbers.length === 0) return null;

  // Helper function to display the code without E prefix if it's in the original format
  const displayCode = (eNumber: ENumber) => {
    const code = eNumber.e_code;
    return code.startsWith('E') ? code : code.replace(/^E/, '');
  };

  return (
    <div className="space-y-4">
      {eNumbers.haramENumbers.length > 0 && (
        <div className="bg-red-50 border border-red-100 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-red-700 font-medium">Haram Food Additives Detected</h4>
              <div className="mt-2 space-y-2">
                {eNumbers.haramENumbers.map(e => (
                  <div key={e.e_code} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-medium text-red-700">{displayCode(e)}</span>
                        <span className="text-red-600 ml-2">({e.name})</span>
                      </div>
                      {e.source && (
                        <span className="text-red-600 text-xs">Source: {e.source}</span>
                      )}
                    </div>
                    {e.description && (
                      <p className="mt-1 text-red-600 text-sm">{e.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {eNumbers.mushboohENumbers.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-yellow-700 font-medium">Food Additives Requiring Verification</h4>
              <div className="mt-2 space-y-2">
                {eNumbers.mushboohENumbers.map(e => (
                  <div key={e.e_code} className="text-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-medium text-yellow-700">{displayCode(e)}</span>
                        <span className="text-yellow-600 ml-2">({e.name})</span>
                      </div>
                      {e.source && (
                        <span className="text-yellow-600 text-xs">Source: {e.source}</span>
                      )}
                    </div>
                    {e.description && (
                      <p className="mt-1 text-yellow-600 text-sm">{e.description}</p>
                    )}
                  </div>
                ))}
              </div>
              <p className="mt-3 text-sm text-yellow-600">
                These additives require verification from the manufacturer or halal certification.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="text-sm text-gray-500">
        <div className="flex items-center gap-1.5">
          <HelpCircle className="w-4 h-4" />
          <span>
            All detected additives: {eNumbers.allENumbers.map(e => displayCode(e)).join(', ')}
          </span>
        </div>
      </div>
    </div>
  );
};